import { required, useRecordContext, useTranslate } from "ra-core";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import ImageEditorField from "../misc/ImageEditorField";
import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Sale } from "../types";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";

const isUrl = (url: string) => {
  if (!url) return;
  const UrlRegex = new RegExp(
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
  );
  if (!UrlRegex.test(url)) {
    return {
      message: "crm.validation.invalid_url",
      args: { _: "Must be a valid URL" },
    };
  }
};

export const CompanyInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4 p-1">
      <CompanyDisplayInputs />
      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <div className="flex flex-col gap-10 flex-1">
          <CompanyContactInputs />
          <CompanyContextInputs />
        </div>
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <div className="flex flex-col gap-8 flex-1">
          <CompanyAddressInputs />
          <CompanyAdditionalInformationInputs />
        </div>
      </div>
    </div>
  );
};

const CompanyDisplayInputs = () => {
  const translate = useTranslate();
  const record = useRecordContext<Company>();
  return (
    <div className="flex gap-4 flex-1 flex-row">
      <ImageEditorField
        source="logo"
        type="avatar"
        width={60}
        height={60}
        emptyText={record?.name.charAt(0)}
        linkPosition="bottom"
      />
      <TextInput
        source="name"
        className="w-full h-fit"
        validate={required()}
        helperText={false}
        placeholder={translate("resources.companies.fields.name", {
          _: "Company name",
        })}
      />
    </div>
  );
};

const CompanyContactInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.companies.field_categories.contact", {
          _: "Company info",
        })}
      </h6>
      <TextInput source="website" helperText={false} validate={isUrl} />
      <TextInput
        source="linkedin_url"
        helperText={false}
        validate={isLinkedinUrl}
      />
      <TextInput source="phone_number" helperText={false} />
    </div>
  );
};

const ACCOUNT_TYPES = [
  { id: 'Vendor', name: 'Vendor' },
  { id: 'Customer', name: 'Customer' },
  { id: 'Both', name: 'Both' },
  { id: 'Shipping', name: 'Shipping' },
];

const ACCOUNT_STATUSES = [
  { id: 'Active', name: 'Active' },
  { id: 'Inactive', name: 'Inactive' },
  { id: 'Lead-Account', name: 'Lead Account' },
  { id: 'OutofBusiness', name: 'Out of Business' },
  { id: 'Do Not Contact', name: 'Do Not Contact' },
  { id: 'On Hold', name: 'On Hold' },
  { id: 'With Competitor', name: 'With Competitor' },
  { id: 'Duplicate', name: 'Duplicate' },
  { id: 'Retired', name: 'Retired' },
];

const PAYMENT_TERMS = [
  { id: 'Prepay', name: 'Prepay' },
  { id: 'Pay Upon Loading', name: 'Pay Upon Loading' },
  { id: '7 Days', name: '7 Days' },
  { id: '10 Days', name: '10 Days' },
  { id: '15 Days', name: '15 Days' },
  { id: '20 Days', name: '20 Days' },
  { id: '25 Days', name: '25 Days' },
  { id: '30 Days', name: '30 Days' },
  { id: '45 Days', name: '45 Days' },
  { id: '60 Days', name: '60 Days' },
  { id: '50-50 Prepay - Upon Loading', name: '50-50 Prepay / Upon Loading' },
  { id: '50-50 Prepay - 15 Days', name: '50-50 Prepay / 15 Days' },
  { id: '50-50 Prepay - 20 Days', name: '50-50 Prepay / 20 Days' },
  { id: '75 Prepay - 25 Pay Upon Loading', name: '75% Prepay / 25% Upon Loading' },
  { id: 'Prepay or Offsets', name: 'Prepay or Offsets' },
  { id: 'Due on Receipt', name: 'Due on Receipt' },
  { id: 'Credit', name: 'Credit' },
];

const PAYMENT_METHODS = [
  { id: 'Wire', name: 'Wire' },
  { id: 'ACH', name: 'ACH' },
  { id: 'Cheque', name: 'Cheque' },
];

const CUSTOMER_REGIONS = [
  { id: 'Not a Customer', name: 'Not a Customer' },
  { id: 'Africa - Central', name: 'Africa - Central' },
  { id: 'Africa - Northern', name: 'Africa - Northern' },
  { id: 'Africa - Southern', name: 'Africa - Southern' },
  { id: 'Asia - Central', name: 'Asia - Central' },
  { id: 'Asia - China', name: 'Asia - China' },
  { id: 'Asia - Pacific', name: 'Asia - Pacific' },
  { id: 'Asia - South Pacific', name: 'Asia - South Pacific' },
  { id: 'Canada - Atlantic', name: 'Canada - Atlantic' },
  { id: 'Canada - Ontario', name: 'Canada - Ontario' },
  { id: 'Canada - Ontario - GTA', name: 'Canada - Ontario - GTA' },
  { id: 'Canada - North', name: 'Canada - North' },
  { id: 'Canada - Pacific', name: 'Canada - Pacific' },
  { id: 'Canada - Prairies', name: 'Canada - Prairies' },
  { id: 'Canada - Quebec', name: 'Canada - Quebec' },
  { id: 'Caribbean', name: 'Caribbean' },
  { id: 'Central America', name: 'Central America' },
  { id: 'Europe - Central', name: 'Europe - Central' },
  { id: 'Europe - Eastern', name: 'Europe - Eastern' },
  { id: 'Europe - Scandinavia', name: 'Europe - Scandinavia' },
  { id: 'Europe - Southern', name: 'Europe - Southern' },
  { id: 'Europe - UK', name: 'Europe - UK' },
  { id: 'Europe - Western', name: 'Europe - Western' },
  { id: 'India', name: 'India' },
  { id: 'Mexico', name: 'Mexico' },
  { id: 'Middle East', name: 'Middle East' },
  { id: 'Oceania', name: 'Oceania' },
  { id: 'Pakistan', name: 'Pakistan' },
  { id: 'Pakistan - EDZ', name: 'Pakistan - EDZ' },
  { id: 'Pakistan - Local Mkt', name: 'Pakistan - Local Mkt' },
  { id: 'Russia', name: 'Russia' },
  { id: 'South America', name: 'South America' },
  { id: 'United Arab Emirates', name: 'United Arab Emirates' },
  { id: 'USA - Alaska', name: 'USA - Alaska' },
  { id: 'USA - Border', name: 'USA - Border' },
  { id: 'USA - Central', name: 'USA - Central' },
  { id: 'USA - Great Lakes', name: 'USA - Great Lakes' },
  { id: 'USA - NorthEast', name: 'USA - NorthEast' },
  { id: 'USA - Pacific', name: 'USA - Pacific' },
  { id: 'USA - South East', name: 'USA - South East' },
  { id: 'USA - South West', name: 'USA - South West' },
  { id: 'USA - Western', name: 'USA - Western' },
];

const VENDOR_REGIONS = [
  { id: 'Not a Vendor', name: 'Not a Vendor' },
  { id: 'Africa - Central', name: 'Africa - Central' },
  { id: 'Africa - Northern', name: 'Africa - Northern' },
  { id: 'Africa - Southern', name: 'Africa - Southern' },
  { id: 'Asia - Central', name: 'Asia - Central' },
  { id: 'Asia - China', name: 'Asia - China' },
  { id: 'Asia - Pacific', name: 'Asia - Pacific' },
  { id: 'Asia - South Pacific', name: 'Asia - South Pacific' },
  { id: 'Canada - Atlantic', name: 'Canada - Atlantic' },
  { id: 'Canada - Ontario', name: 'Canada - Ontario' },
  { id: 'Canada - Ontario - GTA', name: 'Canada - Ontario - GTA' },
  { id: 'Canada - North', name: 'Canada - North' },
  { id: 'Canada - Pacific', name: 'Canada - Pacific' },
  { id: 'Canada - Prairies', name: 'Canada - Prairies' },
  { id: 'Canada - Quebec', name: 'Canada - Quebec' },
  { id: 'Caribbean', name: 'Caribbean' },
  { id: 'Central America', name: 'Central America' },
  { id: 'Europe - Central', name: 'Europe - Central' },
  { id: 'Europe - Eastern', name: 'Europe - Eastern' },
  { id: 'Europe - Scandinavia', name: 'Europe - Scandinavia' },
  { id: 'Europe - Southern', name: 'Europe - Southern' },
  { id: 'Europe - UK', name: 'Europe - UK' },
  { id: 'Europe - Western', name: 'Europe - Western' },
  { id: 'India', name: 'India' },
  { id: 'Mexico', name: 'Mexico' },
  { id: 'Middle East', name: 'Middle East' },
  { id: 'Oceania', name: 'Oceania' },
  { id: 'Pakistan', name: 'Pakistan' },
  { id: 'Pakistan - EDZ', name: 'Pakistan - EDZ' },
  { id: 'Pakistan - Local Mkt', name: 'Pakistan - Local Mkt' },
  { id: 'Russia', name: 'Russia' },
  { id: 'South America', name: 'South America' },
  { id: 'United Arab Emirates', name: 'United Arab Emirates' },
  { id: 'USA - Alaska', name: 'USA - Alaska' },
  { id: 'USA - Border', name: 'USA - Border' },
  { id: 'USA - Central', name: 'USA - Central' },
  { id: 'USA - Great Lakes', name: 'USA - Great Lakes' },
  { id: 'USA - NorthEast', name: 'USA - NorthEast' },
  { id: 'USA - Pacific', name: 'USA - Pacific' },
  { id: 'USA - South East', name: 'USA - South East' },
  { id: 'USA - South West', name: 'USA - South West' },
  { id: 'USA - Western', name: 'USA - Western' },
];

const CompanyContextInputs = () => {
  const translate = useTranslate();
  const { companySectors } = useConfigurationContext();
  const translatedSizes = sizes.map((size) => ({
    ...size,
    name: getTranslatedCompanySizeLabel(size, translate),
  }));
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.companies.field_categories.context", {
          _: "Context",
        })}
      </h6>
      <SelectInput
        source="sector"
        choices={companySectors}
        optionText="label"
        optionValue="value"
        helperText={false}
      />
      <SelectInput source="size" choices={translatedSizes} helperText={false} />
      <TextInput source="revenue" helperText={false} />
      <TextInput source="tax_identifier" helperText={false} />
      <SelectInput
        source="account_type"
        choices={ACCOUNT_TYPES}
        helperText={false}
        label="Account Type"
      />
      <SelectInput
        source="account_status"
        choices={ACCOUNT_STATUSES}
        helperText={false}
        label="Account Status"
      />
      <SelectInput
        source="customer_region"
        choices={CUSTOMER_REGIONS}
        optionText="name"
        optionValue="id"
        helperText={false}
        label="Customer Region"
      />
      <SelectInput
        source="vendor_region"
        choices={VENDOR_REGIONS}
        optionText="name"
        optionValue="id"
        helperText={false}
        label="Vendor Region"
      />
    </div>
  );
};

const CompanyAddressInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.companies.field_categories.address", {
          _: "Address",
        })}
      </h6>
      <TextInput source="address" helperText={false} />
      <TextInput source="city" helperText={false} />
      <TextInput source="zipcode" helperText={false} />
      <TextInput source="state_abbr" helperText={false} />
      <TextInput source="country" helperText={false} />
    </div>
  );
};

const CompanyAdditionalInformationInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.companies.field_categories.additional_info", {
          _: "Additional information",
        })}
      </h6>
      <TextInput source="netsuite_id" helperText={false} label="NetSuite ID" />
      <SelectInput
        source="customer_terms"
        choices={PAYMENT_TERMS}
        helperText={false}
        label="Customer Terms"
      />
      <SelectInput
        source="vendor_terms"
        choices={PAYMENT_TERMS}
        helperText={false}
        label="Vendor Terms"
      />
      <SelectInput
        source="payment_method"
        choices={PAYMENT_METHODS}
        helperText={false}
        label="Payment Method"
      />
      <TextInput source="description" multiline helperText={false} />
      <ArrayInput source="context_links" helperText={false}>
        <SimpleFormIterator disableReordering fullWidth getItemLabel={false}>
          <TextInput
            source=""
            label={false}
            helperText={false}
            validate={isUrl}
          />
        </SimpleFormIterator>
      </ArrayInput>
      <ReferenceInput
        source="sales_id"
        reference="sales"
        filter={{
          "disabled@neq": true,
        }}
      >
        <SelectInput helperText={false} optionText={saleOptionRenderer} />
      </ReferenceInput>
    </div>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;
